import {css} from 'aphrodite';
import React, {useCallback} from 'react';

import {runAnalysisStep} from 'analysis-tool/AnalysisActions';
import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {isNonRefreshableStepType} from 'analysis-tool/AnalysisTypes';
import {CustomDataViewButton} from 'analysis-tool/data-view/DataViewContext';
import {dataViewToolbarStyles} from 'analysis-tool/data-view/DataViewToolbar';
import {isNonReRunnableStepType} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import isArchived from 'archive/isArchived';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getInputStepDatas} from 'pipelines/StepConnectionUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {withLoadingScreen} from 'util/Promises';
interface RerunStepButtonProps {
  analysisId: string;
  stepId: string;
  stepType: PipelineStepType;
  isStepRunning: boolean;
  isAnalysisArchived: boolean;
}

/** @package Exported for tests */
export function RerunStepButton({
  analysisId,
  stepId,
  stepType,
  isStepRunning,
  isAnalysisArchived,
}: RerunStepButtonProps): JSX.Element {
  const isDisabled = isAnalysisArchived || isStepRunning;

  const helpText = isAnalysisArchived
    ? getArchivedTooltipMessage({id: analysisId})
    : isStepRunning
      ? 'Step is currently running'
      : 'Rerun step';

  const handleClick = useCallback(async () => {
    recordAnalysisAction('RERUN_STEP', {
      analysisId,
      stepId,
      stepType,
    });
    await withLoadingScreen(runAnalysisStep(analysisId, {stepId}), 'Rerunning step…');
  }, [analysisId, stepId, stepType]);

  return (
    <Tooltip text={helpText}>
      <Button
        aria-label="Rerun step"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={css(dataViewToolbarStyles.button)}
        disabled={isDisabled}
        icon="rotate-right"
        onClick={isDisabled ? undefined : handleClick}
        size={ButtonSize.MINI}
      />
    </Tooltip>
  );
}

/** @package Exported for tests */
export function canRefreshView(view: PipelineViewGraphQL): boolean {
  if (view.lineage.length === 0) {
    return false;
  }

  const sourceStepType = view.lineage[0].step.stepType;

  if (getInputStepDatas(sourceStepType).length > 0) {
    return false;
  }

  if (isNonReRunnableStepType(sourceStepType) || isNonRefreshableStepType(sourceStepType)) {
    return false;
  }

  return true;
}

export default function getRerunStepAction(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL
): CustomDataViewButton {
  if (!canRefreshView(view) || isAnalysisReadOnly(analysis)) {
    return null;
  }

  const firstStep = view.lineage[0].step;
  return (
    <RerunStepButton
      analysisId={analysis.id}
      stepId={firstStep.id}
      stepType={firstStep.stepType}
      isStepRunning={firstStep.statusCode === PipelineStepStatusCode.RUNNING}
      isAnalysisArchived={isArchived(analysis)}
    />
  );
}
